import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay'
import { CommonModule, registerLocaleData } from '@angular/common'
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import localeEn from '@angular/common/locales/en'
import localePt from '@angular/common/locales/pt'
import localeEs from '@angular/common/locales/es'
import localePtPt from '@angular/common/locales/pt-PT'
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MAT_DATE_LOCALE } from '@angular/material/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { NgxChartsModule } from '@swimlane/ngx-charts'
import { AlertModule, AuthGuard, AuthGuardAdmin, AuthGuardAttendant, AuthGuardSupervisor, CONSTANTS, customHttpProvider, ENV, HttpErrorInterceptor, JwtService, LanguageHeaderInterceptor, MultiTranslateHttpLoader, SharedModule, TrendMissingTranslationHandler } from 'lib-trend-core'
import { CHAT_PUSH_CONFIG, CHAT_SURFACE, ChatClientSurfaceEnum, ChatPushConfig } from 'lib-trend-core/chat'
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask'
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { GlobalConfig, ToastrModule } from 'ngx-toastr'
import { LanguageService } from './i18n/language.service'
import { Constants } from '../environments/constantes'
import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { AgentsModule } from './modules/agents/agents.module'
import { AttendanceModule } from './modules/attendance/attendance.module'
import { BillingRoutingModule } from './modules/billing/billing-routing.module'
import { CampaignModule } from './modules/campaign/campaign.module'
import { ChannelModule } from './modules/channel/channel.module'
import { ChatbotModule } from './modules/chatbot/chatbot.module'
import { ContactGroupModule } from './modules/contact-group/contact-group.module'
import { ContactModule } from './modules/contact/contact.module'
import { CrmModule } from './modules/crm/crm.module'
import { CustomFieldModule } from './modules/custom-field/custom-field.module'
import { DashboardModule } from './modules/dashboard/dashboard.module'
import { DepartmentModule } from './modules/department/department.module'
import { FlowModule } from './modules/flow/flow.module'
import { IntegrationsModule } from './modules/integrations/integrations.module'
import { KanbanModule } from './modules/kanban/kanban.module'
import { LayoutModule } from './modules/layout/layout.module'
import { LeadSourceModule } from './modules/lead-source/lead-source.module'
import { NotificationModule } from './modules/notification/notification.module'
import { RedirectModule } from './modules/redirect/redirect.module'
import { ReportModule } from './modules/report/report.module'
import { ResetPasswordModule } from './modules/reset-password/reset-password.module'
import { ResponseModule } from './modules/response/response.module'
import { SingupRoutingModule } from './modules/singup/singup-routing.module'
import { TabulationModule } from './modules/tabulation/tabulation.module'
import { TagModule } from './modules/tag/tag.module'
import { TaskModule } from './modules/task/task.module'
import { TrackingModule } from './modules/tracking/tracking.module'
import { UpdatePasswordModule } from './modules/update-password/update-password.module'
import { UserModule } from './modules/user/user.module'
import { WhatsappSignupModule } from './modules/whatsapp-signup/whatsapp-signup.module'

const maskConfig: Partial<NgxMaskConfig> = {
  validation: false,
};

const toastGlobalConfig: Partial<GlobalConfig> = {
  preventDuplicates: true,
  maxOpened: 3
};

// Internal Chat web push wiring (SPEC-2026-09-internal-chat-pwa, T-26). The product has
// no Angular service worker, so its messaging worker keeps the default root scope.
const chatPushConfig: ChatPushConfig = {
  apiUrl: environment.apiUrl,
  firebase: environment.firebase,
  serviceWorkerUrl: '/firebase-messaging-sw.js',
}

registerLocaleData(localeEn, 'en')
registerLocaleData(localePt, 'pt-BR')
registerLocaleData(localePtPt, 'pt-PT')
registerLocaleData(localeEs, 'es')

// Multi-source catalog loader (app + lib-core + lib-chat), built with a clean HttpClient
// (via HttpBackend) so it bypasses CustomHttp's API-base prefixing. `lib-chat` carries
// the Internal Chat keys shared with the chat app (SPEC-2026-09-internal-chat-pwa).
export function httpTranslateLoaderFactory(handler: HttpBackend): TranslateLoader {
  return new MultiTranslateHttpLoader(new HttpClient(handler), [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/lib-core.', suffix: '.json' },
    { prefix: './assets/i18n/lib-chat.', suffix: '.json' },
  ])
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule.forRoot({
            defaultLanguage: 'pt-BR',
            useDefaultLang: true,
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoaderFactory,
                deps: [HttpBackend],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: TrendMissingTranslationHandler,
            },
        }),
        AppRoutingModule,
        RedirectModule,
        NgxChartsModule,
        LayoutModule,
        SharedModule,
        DepartmentModule,
        DashboardModule,
        ReportModule,
        IntegrationsModule,
        TagModule,
        UserModule,
        ResponseModule,
        ChannelModule,
        AlertModule,
        FlowModule,
        ContactGroupModule,
        ContactModule,
        CampaignModule,
        ChatbotModule,
        UpdatePasswordModule,
        ResetPasswordModule,
        NotificationModule,
        AttendanceModule,
        WhatsappSignupModule,
        ToastrModule.forRoot(toastGlobalConfig),
        EffectsModule.forRoot(),
        StoreModule.forRoot(),
        CustomFieldModule,
        TabulationModule,
        LeadSourceModule,
        TrackingModule,
        CrmModule,
        TaskModule,
        AgentsModule,
        KanbanModule,
        SingupRoutingModule,
        BillingRoutingModule,
        (!environment.production
            ? StoreDevtoolsModule.instrument({
                name: 'Attendance DevTools',
                maxAge: 25, // Retains last 25 states
                logOnly: environment.production,
                connectInZone: true,
            })
            : [])], providers: [
        AuthGuard,
        AuthGuardAdmin,
        AuthGuardAttendant,
        AuthGuardSupervisor,
        provideEnvironmentNgxMask(maskConfig),
        { provide: LOCALE_ID, useFactory: (lang: LanguageService) => lang.currentLang, deps: [LanguageService] },
        { provide: HTTP_INTERCEPTORS, useClass: JwtService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageHeaderInterceptor, multi: true },
        { provide: ENV, useValue: environment },
        { provide: CONSTANTS, useValue: Constants },
        { provide: CHAT_SURFACE, useValue: ChatClientSurfaceEnum.WEB_APP },
        { provide: CHAT_PUSH_CONFIG, useValue: chatPushConfig },
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        // CDK 21 renders overlays in the browser top layer by default; keeping them in the overlay
        // container lets SweetAlert2 and toasts still stack above Material dialogs.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        provideAnimationsAsync(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        // After provideHttpClient: the last HttpClient provider wins, and it must be CustomHttp.
        customHttpProvider,
    ] })
export class AppModule { }
